import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { get } from '@truefit/http-utils';
import { useAuth, isSignedIn } from '@/features/scaffolding/components/AuthContext';

interface TranscodingProfileConfig {
  portrait: number;
  landscape: number;
  /** Absent on a business-api older than C360-3094. */
  portraitZoom?: number;
}

interface TranscodingProfileContextValue {
  profiles: TranscodingProfileConfig | null;
  isLoading: boolean;
  error: Error | null;
  /**
   * Extra tracking zoom the exporter applies to 9:16 clips (C360-3094), so the
   * 9:16 overlay can outline the region that actually gets exported. `1` means
   * "framed exactly like 16:9" — also what we report before the config loads
   * or against an API that doesn't send it, which draws the old full-height
   * band rather than guessing a crop the exporter isn't doing.
   */
  portraitZoom: number;
  getMode: (profileId: number) => 'portrait' | 'landscape' | null;
  getModeId: (mode: 'portrait' | 'landscape') => number | null;
  /**
   * Re-fetch the id map. The assignment ids can change server-side while a
   * tab is open (profiles reseeded / reassigned); consumers that encounter an
   * id the current map can't resolve should call this to heal the tab.
   */
  refetch: () => Promise<void>;
}

const TranscodingProfileContext = createContext<TranscodingProfileContextValue | undefined>(
  undefined
);

export const TranscodingProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);
  const [profiles, setProfiles] = useState<TranscodingProfileConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await get<TranscodingProfileConfig>(
        '/v1.0/transcoding-profiles-config'
      );
      console.log('[TranscodingProfileContext] Loaded profiles:', response.data);
      setProfiles(response.data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load transcoding profiles');
      setError(error);
      console.error('[TranscodingProfileContext] Error loading transcoding profiles:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    fetchProfiles();
  }, [isAuthReady, fetchProfiles]);

  const getMode = useCallback(
    (profileId: number): 'portrait' | 'landscape' | null => {
      if (!profiles) return null;
      if (profileId === profiles.portrait) return 'portrait';
      if (profileId === profiles.landscape) return 'landscape';
      return null;
    },
    [profiles],
  );

  const portraitZoom =
    profiles?.portraitZoom && profiles.portraitZoom >= 1 ? profiles.portraitZoom : 1;

  const getModeId = useCallback(
    (mode: 'portrait' | 'landscape'): number | null => {
      if (!profiles) return null;
      return mode === 'portrait' ? profiles.portrait : profiles.landscape;
    },
    [profiles],
  );

  const value = useMemo<TranscodingProfileContextValue>(
    () => ({
      profiles,
      isLoading,
      error,
      portraitZoom,
      getMode,
      getModeId,
      refetch: fetchProfiles,
    }),
    [profiles, isLoading, error, portraitZoom, getMode, getModeId, fetchProfiles],
  );

  return (
    <TranscodingProfileContext.Provider value={value}>
      {children}
    </TranscodingProfileContext.Provider>
  );
};

export const useTranscodingProfiles = (): TranscodingProfileContextValue => {
  const context = useContext(TranscodingProfileContext);
  if (!context) {
    throw new Error(
      'useTranscodingProfiles must be used within a TranscodingProfileProvider'
    );
  }
  return context;
};
